import { ErrorResponse } from '@graphql';
import { KNOWN_ERROR_MESSAGES as ERROR, KnownErrors } from '@utils/constants';

type ErrorResult = { message: string; code?: number };

type ErrorWithMessage = {
  message: string;
};

const isErrorWithMessage = (error: unknown): error is ErrorWithMessage =>
  typeof error === 'object' &&
  error !== null &&
  'message' in error &&
  typeof (error as Record<string, unknown>).message === 'string';

const toErrorWithMessage = (maybeError: unknown): ErrorWithMessage => {
  if (isErrorWithMessage(maybeError)) {
    return maybeError;
  }

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback in case there's an error stringifying the maybeError
    // like with circular references for example.
    return new Error(String(maybeError));
  }
};

export const getErrorMessage = (error: unknown) => toErrorWithMessage(error).message;

const defaultError = {
  message: ERROR.DEFAULT,
};

const errorsMap: { [key: string]: string } = {
  [KnownErrors.NotFound]: ERROR.INVALID_QUOTE,
  [KnownErrors.ValidationError]: ERROR.VALIDATION_ERROR,
  [KnownErrors.QuoteExpired]: ERROR.QUOTE_EXPIRED,
  [KnownErrors.InvalidPackageSelection]: ERROR.INVALID_PACKAGE_SELECTION,
};

export function getErrorDetails(error: ErrorResponse): ErrorResult {
  const graphqlError = error?.response?.errors?.[0];

  if (!graphqlError) {
    return {
      message: getErrorMessage(error) || ERROR.DEFAULT,
    };
  }

  const errorMessage = getErrorMessage(graphqlError);
  const errorDetails = graphqlError?.extensions;

  if (errorsMap[errorMessage]) {
    return {
      message: errorsMap[errorMessage],
    };
  }

  if (errorDetails?.response) {
    const { status, body } = errorDetails.response;
    switch (status) {
      case 500:
        return { code: status, message: ERROR.INTERNAL_SERVER_ERROR };
      case 404:
        return { code: status, message: ERROR.USER_NOT_FOUND };
      default:
        return { code: status, message: body?.code || body?.message || ERROR.DEFAULT };
    }
  }

  if (errorDetails?.code) {
    switch (errorDetails.code) {
      case 'UNAUTHORIZED':
        return {
          code: errorDetails.statusCode,
          message: errorDetails.message?.includes('suspended') ? ERROR.USER_SUSPENDED : ERROR.FORBIDDEN,
        };
      case 'UNAUTHENTICATED':
        return { code: errorDetails.statusCode, message: 'UNAUTHENTICATED' };
      case 'FORBIDDEN':
        return { code: 403, message: ERROR.FORBIDDEN };
      default:
        return defaultError;
    }
  }

  return defaultError;
}
